import React, { useEffect, useState, useRef } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
interface Option {
  value: string;
  label: string;
}
interface MultiSelectProps {
  label?: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  error,
  disabled = false
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };
  const removeOption = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };
  const selectedOptions = options.filter(opt => value.includes(opt.value));
  return <div className="w-full space-y-1.5" ref={containerRef} data-id="element-1207">
      {label && <label className="text-sm font-medium text-gray-700" data-id="element-1208">{label}</label>}

      <div className={`
          relative min-h-[40px] w-full rounded-md border bg-white px-3 py-2 text-sm cursor-pointer
          ${error ? 'border-red-500 focus-within:ring-red-500' : 'border-gray-300 focus-within:ring-blue-500'}
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-gray-400'}
          focus-within:ring-2 focus-within:border-transparent
        `} onClick={() => !disabled && setIsOpen(!isOpen)} data-id="element-1209">
        <div className="flex flex-wrap gap-2 pr-8" data-id="element-1210">
          {selectedOptions.length > 0 ? selectedOptions.map(option => <span key={option.value} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1211">
                {option.label}
                <button type="button" onClick={e => removeOption(e, option.value)} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-blue-200 focus:outline-none" data-id="element-1212">
                  <X className="h-3 w-3" data-id="element-1213" />
                </button>
              </span>) : <span className="text-gray-400" data-id="element-1214">{placeholder}</span>}
        </div>

        <div className="absolute right-3 top-3 pointer-events-none" data-id="element-1215">
          <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-1216" />
        </div>
      </div>

      {isOpen && !disabled && <div className="absolute z-10 mt-1 w-full max-w-md bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" data-id="element-1217">
          {options.length > 0 ? options.map(option => <div key={option.value} className={`
                  cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50
                  ${value.includes(option.value) ? 'text-blue-900 bg-blue-50' : 'text-gray-900'}
                `} onClick={() => handleSelect(option.value)} data-id="element-1218">
                <span className={`block truncate ${value.includes(option.value) ? 'font-semibold' : 'font-normal'}`} data-id="element-1219">
                  {option.label}
                </span>
                {value.includes(option.value) && <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600" data-id="element-1220">
                    <Check className="h-4 w-4" data-id="element-1221" />
                  </span>}
              </div>) : <div className="py-2 px-3 text-gray-500 text-center" data-id="element-1222">
              No options available
            </div>}
        </div>}

      {error && <p className="text-xs text-red-500 font-medium" data-id="element-1223">{error}</p>}
    </div>;
}